import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authErrorText, supabase } from "@/lib/supabase";

// Credential exchange is one-shot. Account verification can safely be retried.
export default function AuthCallback() {
  const hasProcessed = useRef(false), mounted = useRef(false), autoRetried = useRef(false);
  const navigate = useNavigate();
  const { refresh, loading, checking, verificationError, isAuthenticated, user, org } = useAuth();
  const [phase, setPhase] = useState("link");
  const [error, setError] = useState("");

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const linkError = params.get("error_description"), code = params.get("code");
      const tokenHash = params.get("token_hash"), type = params.get("type") || "email";
      window.history.replaceState(null, "", window.location.pathname);
      try {
        let err = null;
        if (linkError) err = { message: linkError };
        else if (code) ({ error: err } = await supabase.auth.exchangeCodeForSession(code));
        else if (tokenHash) ({ error: err } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type }));
        else err = { message: "This sign-in link is incomplete." };
        if (!mounted.current) return;
        if (err) return setError(authErrorText(err));
        if (type === "invite") {
          setPhase("invite");
          return navigate("/auth/reset", { replace: true, state: { invited: true } });
        }
        setPhase("account");
        // null also means this check was superseded, not that the link failed.
        // Follow the current verified context below instead of an obsolete result.
        await refresh();
      } catch (failure) {
        if (mounted.current) setError(authErrorText(failure));
      }
    };
    run();
  }, [navigate, refresh]);

  useEffect(() => {
    if (phase !== "account" || loading || checking || !isAuthenticated) return;
    navigate(org ? "/home" : "/onboarding", { replace: true, state: { user } });
  }, [phase, loading, checking, isAuthenticated, org, user, navigate]);

  useEffect(() => {
    if (phase !== "account" || loading || checking || isAuthenticated || !verificationError?.retryable || autoRetried.current) return;
    autoRetried.current = true;
    // One bounded read-only recovery. Never replay OAuth/OTP or sign the user out.
    const timer = setTimeout(() => { refresh(); }, 500);
    return () => clearTimeout(timer);
  }, [phase, loading, checking, isAuthenticated, verificationError?.retryable, refresh]);

  const accountIssue = phase === "account" && !loading && !checking && !isAuthenticated && !!verificationError;
  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-background" data-testid="auth-callback">
      {error || accountIssue ? (
        <div className="max-w-md w-full text-center space-y-5">
          <img src="/legacystack-mark.png" alt="" className="h-10 w-10 mx-auto" />
          <h1 className="font-display text-3xl">{error ? "That link didn't work." : "Sign-in finished. Your account couldn't be loaded."}</h1>
          <p className="text-sm text-muted-foreground" data-testid="auth-callback-error">{error || "Try loading your account again. You don't need to repeat the sign-in link."}</p>
          {accountIssue && <button type="button" className="btn-gold inline-flex" onClick={() => refresh()}>Retry account loading</button>}
          <Link to="/auth" className={error ? "btn-gold inline-flex" : "btn-outline inline-flex"}>Back to sign in</Link>
        </div>
      ) : (
        <div role="status" className="text-center space-y-4"><img src="/legacystack-mark.png" alt="" className="h-10 w-10 mx-auto breathe" /><p>{phase === "account" ? "Loading your account…" : "Finishing sign-in…"}</p></div>
      )}
    </div>
  );
}
